import { Fragment, useEffect, useRef, useState } from 'react'
import type { CSSProperties } from 'react'
import { useReducedMotion } from 'motion/react'
import type { GestureCue, MediaClip } from '../config/media'
import styles from './MediaFrame.module.css'

interface MediaFrameProps {
  /** Footage entry from `src/config/media.ts`. When `src` is null a neutral
   *  placeholder is shown — no fabricated product UI. */
  clip?: MediaClip
  /** Accessible description of what the footage demonstrates. */
  description: string
  /** Optional small caption rendered under the frame. */
  caption?: string
  /** CSS aspect-ratio value. Defaults to the capture ratio. */
  aspectRatio?: string
  /** Load eagerly (above the fold). Others lazy-mount when scrolled near. */
  priority?: boolean
}

type Phase = 'idle' | 'playing'

let activeVideo: HTMLVideoElement | null = null
const EMPTY_CUES: GestureCue[] = []

/**
 * Restrained frame for real product footage.
 *
 * Playback: muted + inline, starts when the frame is meaningfully in view, and
 * loops while visible. It pauses offscreen and restarts from the beginning when
 * it comes back so the viewer always sees the complete action.
 *
 * `prefers-reduced-motion`: no autoplay; native controls, poster shown, the
 * viewer starts it.
 */
export function MediaFrame({
  clip,
  description,
  caption,
  aspectRatio = '16 / 10',
  priority = false,
}: MediaFrameProps) {
  const reduceMotion = useReducedMotion()
  const rootRef = useRef<HTMLDivElement>(null)
  const videoRef = useRef<HTMLVideoElement>(null)
  const phaseRef = useRef<Phase>('idle')
  const visibleRef = useRef(false)
  const activeCueIndexRef = useRef<number | null>(null)

  const [mounted, setMounted] = useState(
    () => priority || typeof IntersectionObserver === 'undefined'
  )
  const [phase, setPhase] = useState<Phase>('idle')
  const [activeCueIndex, setActiveCueIndex] = useState<number | null>(null)
  const [ready, setReady] = useState(false)

  useEffect(() => {
    phaseRef.current = phase
  }, [phase])

  const hasVideo = Boolean(clip?.src)
  const cues = clip?.cues ?? EMPTY_CUES
  const activeCue = reduceMotion
    ? cues[0]
    : activeCueIndex === null
      ? undefined
      : cues[activeCueIndex]

  // Lazy-mount the <video> when it gets near the viewport.
  useEffect(() => {
    if (mounted || !hasVideo) return
    const node = rootRef.current
    if (!node) return
    const io = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          setMounted(true)
          io.disconnect()
        }
      },
      { rootMargin: '240px' }
    )
    io.observe(node)
    return () => io.disconnect()
  }, [mounted, hasVideo])

  // Play only when meaningfully in view. Returning to a frame restarts its
  // story, while the native loop keeps it moving if the viewer stays there.
  useEffect(() => {
    if (!mounted || reduceMotion) return
    const node = rootRef.current
    const video = videoRef.current
    if (!node || !video || typeof IntersectionObserver === 'undefined') return

    const io = new IntersectionObserver(
      (entries) => {
        const visible = entries.some((e) => e.intersectionRatio >= 0.35)
        if (visible && !visibleRef.current) {
          video.currentTime = 0
          activeCueIndexRef.current = null
          setActiveCueIndex(null)
          video
            .play()
            .catch(() => {})
        } else if (!visible && visibleRef.current && phaseRef.current === 'playing') {
          video.pause()
        }
        visibleRef.current = visible
      },
      { threshold: [0, 0.35], rootMargin: '-10% 0px -10% 0px' }
    )
    io.observe(node)
    return () => io.disconnect()
  }, [mounted, reduceMotion])

  // Cue visibility is derived from the video clock on every animation frame.
  // This keeps the physical gesture attached to the recorded action through
  // buffering, pauses, tab changes, and native video-loop boundaries.
  useEffect(() => {
    if (phase !== 'playing' || reduceMotion || cues.length === 0) {
      return
    }

    const video = videoRef.current
    if (!video) return

    let frame = 0
    const syncCue = () => {
      const nextIndex = cues.findIndex((cue) => {
        const end = cue.delaySeconds + (cue.durationSeconds ?? 1.8)
        return video.currentTime >= cue.delaySeconds && video.currentTime < end
      })
      const nextCue = nextIndex >= 0 ? nextIndex : null
      if (nextCue !== activeCueIndexRef.current) {
        activeCueIndexRef.current = nextCue
        setActiveCueIndex(nextCue)
      }
      frame = requestAnimationFrame(syncCue)
    }

    syncCue()
    return () => cancelAnimationFrame(frame)
  }, [cues, phase, reduceMotion])

  return (
    <figure className={styles.wrap}>
      <div
        ref={rootRef}
        className={styles.frame}
        style={{ aspectRatio }}
        data-state={hasVideo ? 'media' : 'placeholder'}
      >
        {hasVideo && (reduceMotion || mounted) ? (
          <>
            {clip?.poster ? (
              <img
                className={`${styles.media} ${styles.videoPoster}`}
                src={clip.poster}
                alt=""
                loading={priority ? 'eager' : 'lazy'}
                aria-hidden="true"
              />
            ) : null}
            <video
              ref={videoRef}
              className={`${styles.media} ${styles.video}`}
              data-ready={ready}
              muted
              playsInline
              loop={!reduceMotion}
              controls={Boolean(reduceMotion)}
              preload={priority ? 'auto' : 'metadata'}
              aria-label={description}
              onLoadedData={() => setReady(true)}
              onCanPlay={(event) => {
                setReady(true)
                if (!reduceMotion && visibleRef.current && event.currentTarget.paused) {
                  event.currentTarget.play().catch(() => {})
                }
              }}
              onError={() => setReady(false)}
              onPlay={(event) => {
                const video = event.currentTarget
                if (activeVideo && activeVideo !== video && !activeVideo.paused) {
                  activeVideo.pause()
                }
                activeVideo = video
                setPhase('playing')
              }}
              onPause={() => {
                activeCueIndexRef.current = null
                setActiveCueIndex(null)
                setPhase('idle')
              }}
            >
              {clip?.mobileSrc ? (
                <source src={clip.mobileSrc} media="(max-width: 860px)" type="video/mp4" />
              ) : null}
              <source src={clip?.src ?? undefined} type="video/mp4" />
            </video>
            {activeCue ? (
              <Fragment key={`${activeCueIndex ?? 'reduced'}-${activeCue.delaySeconds}`}>
                <div
                  className={styles.gestureCue}
                  data-kind={activeCue.kind}
                  data-direction={activeCue.direction}
                  data-playing={phase === 'playing'}
                  data-reduced={Boolean(reduceMotion)}
                  style={getCueStyle(activeCue)}
                  aria-hidden="true"
                >
                  <span className={styles.cueTrackpad}>
                    {activeCue.kind === 'shape' ? (
                      <ShapeGlyph />
                    ) : (
                      <>
                        {activeCue.kind === 'swipe' ? (
                          <DirectionGlyph direction={activeCue.direction} />
                        ) : null}
                        <span className={styles.cueContacts}>
                          {Array.from({ length: activeCue.fingers }, (_, fingerIndex) => (
                            <span
                              key={fingerIndex}
                              className={styles.cueFinger}
                              style={{
                                '--finger-y': `${fingerArc(activeCue.fingers, fingerIndex)}px`,
                              } as CSSProperties}
                            />
                          ))}
                        </span>
                      </>
                    )}
                  </span>
                  <span className={styles.cueCopy}>
                    <span className={styles.cueKicker}>Trigger</span>
                    <span className={styles.cueLabel}>{activeCue.label}</span>
                  </span>
                </div>
              </Fragment>
            ) : null}
            {cues.length > 0 ? (
              <span className="sr-only">
                {cues.map((cue) => `Trigger: ${cue.label}.`).join(' ')}
              </span>
            ) : null}
          </>
        ) : hasVideo && clip?.poster ? (
          <img className={styles.media} src={clip.poster} alt={description} loading="lazy" />
        ) : (
          <div className={styles.placeholder}>
            <span className={styles.placeholderMark} aria-hidden="true">
              <TrackpadGlyph />
            </span>
            <p className={styles.placeholderLabel}>Product footage</p>
            <p className={styles.placeholderDesc}>{description}</p>
          </div>
        )}
      </div>
      {caption ? <figcaption className={styles.caption}>{caption}</figcaption> : null}
    </figure>
  )
}

function getCueStyle(cue: GestureCue): CSSProperties {
  const vectors = {
    left: ['9px', '0px', '-9px', '0px'],
    right: ['-9px', '0px', '9px', '0px'],
    up: ['0px', '8px', '0px', '-8px'],
    down: ['0px', '-8px', '0px', '8px'],
  } as const
  const [startX, startY, endX, endY] = vectors[cue.direction ?? 'right']

  return {
    '--cue-duration': `${cue.durationSeconds ?? 1.8}s`,
    '--swipe-start-x': startX,
    '--swipe-start-y': startY,
    '--swipe-end-x': endX,
    '--swipe-end-y': endY,
  } as CSSProperties
}

function fingerArc(count: GestureCue['fingers'], index: number) {
  const center = (count - 1) / 2
  return Math.abs(index - center) * 1.6
}

function DirectionGlyph({ direction }: { direction?: GestureCue['direction'] }) {
  const rotation = { right: 0, down: 90, left: 180, up: -90 }[direction ?? 'right']

  return (
    <svg
      className={styles.cueDirection}
      viewBox="0 0 32 32"
      style={{ '--direction-rotation': `${rotation}deg` } as CSSProperties}
      aria-hidden="true"
    >
      <path d="M7 16h17M19 11l5 5-5 5" />
    </svg>
  )
}

function ShapeGlyph() {
  return (
    <svg className={styles.cueShape} viewBox="0 0 58 42" aria-hidden="true">
      <path d="M39 9C32 5 20 7 19 14c-1 6 7 7 13 8 7 1 10 4 8 8-3 6-15 7-23 2" />
      <circle cx="17" cy="32" r="2.5" />
    </svg>
  )
}

/** Quiet trackpad mark for the empty stage. */
function TrackpadGlyph() {
  return (
    <svg width="46" height="34" viewBox="0 0 46 34" fill="none" xmlns="http://www.w3.org/2000/svg">
      <rect
        x="0.75"
        y="0.75"
        width="44.5"
        height="32.5"
        rx="5"
        stroke="currentColor"
        strokeWidth="1"
      />
      <path
        d="M15 12.5c3.4 0 4.2 9 8 9s4.6-9 8-9"
        stroke="currentColor"
        strokeWidth="1.25"
        strokeLinecap="round"
        opacity="0.55"
      />
    </svg>
  )
}
